import { useCallback, useState } from "react";
import { c, t } from "ttag";

import {
  skipToken,
  useDeepCopyDashboardMutation,
  useGetCollectionQuery,
  useGetDashboardQuery,
} from "metabase/api";
import { Link } from "metabase/common/components/Link";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { CollectionPickerModal } from "metabase/common/components/Pickers/CollectionPicker";
import { useDispatch } from "metabase/redux";
import { addUndo } from "metabase/redux/undo";
import {
  Alert,
  Button,
  Flex,
  Group,
  Icon,
  List,
  Loader,
  Modal,
  Stack,
  Text,
} from "metabase/ui";
import { color } from "metabase/ui/utils/colors";
import * as Urls from "metabase/urls";
import type {
  CollectionId,
  Dashboard,
  DashboardId,
  DeepCopyConflict,
} from "metabase-types/api";

import S from "./DashboardMoveModal.module.css";

// ── types ─────────────────────────────────────────────────────────────────────

type ModalStage =
  | { kind: "picking" }
  | { kind: "cloning"; targetCollectionId: CollectionId }
  | {
      kind: "conflict";
      targetCollectionId: CollectionId;
      conflicts: DeepCopyConflict[];
    }
  | {
      kind: "success";
      newDashboardId: DashboardId;
      targetCollectionId: CollectionId;
    };

// ── sub-components ────────────────────────────────────────────────────────────

const CloningView = () => (
  <Modal.Root opened onClose={() => undefined} size="sm">
    <Modal.Overlay />
    <Modal.Content>
      <Modal.Header>
        <Modal.Title>{t`Cloning with dependencies…`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Stack align="center" gap="md" py="xl">
          <Loader size="xl" />
          <Text c="text-secondary">
            {t`Copying cards and mirroring collection structure. This may take a moment.`}
          </Text>
        </Stack>
      </Modal.Body>
    </Modal.Content>
  </Modal.Root>
);

const ConflictView = ({
  conflicts,
  onForce,
  onCancel,
}: {
  conflicts: DeepCopyConflict[];
  onForce: () => void;
  onCancel: () => void;
}) => (
  <Modal.Root opened onClose={onCancel} size="md">
    <Modal.Overlay />
    <Modal.Content>
      <Modal.Header>
        <Modal.Title>{t`Name conflicts detected`}</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        <Stack gap="md">
          <Alert icon={<Icon name="warning" />} color="warning">
            {t`The following items already exist in the target collection. Proceeding will create duplicates alongside them.`}
          </Alert>
          <List spacing="xs">
            {conflicts.map((c, i) => (
              <List.Item key={i}>
                <Text span fw="bold">
                  {c.name}
                </Text>
                <Text span c="text-secondary">
                  {" "}
                  ({c.type})
                </Text>
              </List.Item>
            ))}
          </List>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onCancel}>{t`Cancel`}</Button>
            <Button color="brand" onClick={onForce}>{t`Proceed anyway`}</Button>
          </Group>
        </Stack>
      </Modal.Body>
    </Modal.Content>
  </Modal.Root>
);

const SuccessToast = ({
  newDashboardId,
  collectionId,
}: {
  newDashboardId: DashboardId;
  collectionId: CollectionId;
}) => {
  const { data: collection } = useGetCollectionQuery({ id: collectionId });
  return (
    <Flex align="center">
      <Icon
        name="clone"
        style={{ marginInlineEnd: "0.25rem" }}
        c="text-primary-inverse"
      />
      {c("{0} is the collection where the cloned dashboard was created")
        .jt`Dashboard cloned into ${
        collection ? (
          <Link
            key="link"
            className={S.CollectionLink}
            to={Urls.collection(collection)}
            style={{ marginInlineStart: ".25em" }}
            color={color("brand")}
          >
            {collection.name}
          </Link>
        ) : null
      } — `}
      <Link
        key="dash"
        to={Urls.dashboard({ id: newDashboardId } as Dashboard)}
        style={{ marginInlineStart: ".25em" }}
        color={color("brand")}
      >
        {t`view it`}
      </Link>
    </Flex>
  );
};

// ── main component ────────────────────────────────────────────────────────────

function DashboardCloneDepsModal({
  dashboard,
  onClose,
}: {
  dashboard: Dashboard;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const [stage, setStage] = useState<ModalStage>({ kind: "picking" });
  const [deepCopy] = useDeepCopyDashboardMutation();

  const runClone = useCallback(
    async (targetCollectionId: CollectionId, force = false) => {
      setStage({ kind: "cloning", targetCollectionId });
      try {
        const result = await deepCopy({
          id: dashboard.id,
          collection_id: targetCollectionId,
          force,
        }).unwrap();
        setStage({
          kind: "success",
          newDashboardId: result.id,
          targetCollectionId,
        });
        dispatch(
          addUndo({
            icon: "clone",
            message: (
              <SuccessToast
                newDashboardId={result.id}
                collectionId={targetCollectionId}
              />
            ),
          }),
        );
        onClose();
      } catch (err: any) {
        const body = err?.data ?? err;
        if (body?.conflicts && Array.isArray(body.conflicts)) {
          setStage({
            kind: "conflict",
            targetCollectionId,
            conflicts: body.conflicts,
          });
        } else {
          // Surface unexpected errors as an undo toast and close
          dispatch(
            addUndo({
              icon: "warning",
              message: t`Clone failed: ${body?.message ?? t`unknown error`}`,
              timeout: 8000,
            }),
          );
          onClose();
        }
      }
    },
    [dashboard.id, deepCopy, dispatch, onClose],
  );

  if (stage.kind === "cloning") {
    return <CloningView />;
  }

  if (stage.kind === "conflict") {
    return (
      <ConflictView
        conflicts={stage.conflicts}
        onForce={() => runClone(stage.targetCollectionId, true)}
        onCancel={onClose}
      />
    );
  }

  // "picking" — show the collection picker
  return (
    <CollectionPickerModal
      title={t`Clone dashboard with dependencies into…`}
      value={{ id: dashboard.collection_id ?? "root", model: "collection" }}
      onChange={(destination) => {
        void runClone(destination.id as CollectionId);
      }}
      options={{
        hasSearch: true,
        hasRecents: true,
        hasRootCollection: true,
        hasPersonalCollections: true,
        hasConfirmButtons: true,
        canCreateCollections: true,
        confirmButtonText: c("verb").t`Clone`,
      }}
      onClose={onClose}
    />
  );
}

export const DashboardCloneDepsModalConnected = ({
  params,
  onClose,
}: {
  params: { slug?: string };
  onClose: () => void;
}) => {
  const id = Urls.extractCollectionId(params.slug);
  const { currentData: dashboard, error } = useGetDashboardQuery(
    id != null ? { id } : skipToken,
  );

  return (
    <LoadingAndErrorWrapper
      loading={id != null && !dashboard}
      error={error}
      noWrapper
    >
      {dashboard && (
        <DashboardCloneDepsModal dashboard={dashboard} onClose={onClose} />
      )}
    </LoadingAndErrorWrapper>
  );
};
